function camelCase(str: string): string {
    return str
        .replace(/[-_\s]+(.)?/g, (_, c: string) => (c ? c.toUpperCase() : ''))
        .replace(/^(.)/, (c) => c.toLowerCase());
}

function merge(target: Record<string, unknown>, ...sources: Record<string, unknown>[]): void {
    for (const src of sources) {
        for (const key of Object.keys(src)) {
            const tv = target[key];
            const sv = src[key];
            if (
                sv &&
                typeof sv === 'object' &&
                !Array.isArray(sv) &&
                tv &&
                typeof tv === 'object' &&
                !Array.isArray(tv)
            ) {
                merge(tv as Record<string, unknown>, sv as Record<string, unknown>);
            } else {
                target[key] = sv;
            }
        }
    }
}

export interface JsonApiResponse {
    data: JsonApiObject | JsonApiObject[];
    included?: JsonApiObject[];
    links?: Record<string, string>;
    meta?: Record<string, unknown>;
}

export interface JsonApiObject {
    id: string;
    type: string;
    attributes: Record<string, unknown>;
    relationships?: JsonApiRelationships;
    links?: Record<string, string>;
    meta?: Record<string, unknown>;
}

export type JsonApiRelationships = Record<
    string,
    {
        data: JsonApiReference | JsonApiReference[] | null;
        links?: Record<string, string>;
        meta?: Record<string, unknown>;
    }
>;

interface JsonApiReference {
    id: string;
    type: string;
}

interface NormalizeOptions {
    filterEndpoint?: boolean;
    camelizeKeys?: boolean;
    camelizeTypeValues?: boolean;
    endpoint?: string;
}

function isDate(value: unknown): value is Date {
    return Object.prototype.toString.call(value) === '[object Date]';
}

function camelizeNestedKeys(value: unknown): unknown {
    if (value === null || typeof value !== 'object' || isDate(value)) return value;
    if (Array.isArray(value)) return value.map(camelizeNestedKeys);
    const copy: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
        copy[camelCase(k)] = camelizeNestedKeys((value as Record<string, unknown>)[k]);
    }
    return copy;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- complex logic, refactor deferred
function extractRelationships(
    relationships: JsonApiRelationships,
    { camelizeKeys, camelizeTypeValues }: { camelizeKeys: boolean; camelizeTypeValues: boolean },
): Record<string, unknown> {
    const ret: Record<string, unknown> = {};
    for (const key of Object.keys(relationships)) {
        const rel = relationships[key];
        const name = camelizeKeys ? camelCase(key) : key;
        const entry: Record<string, unknown> = {};
        if (typeof rel.data !== 'undefined') {
            if (Array.isArray(rel.data)) {
                entry.data = rel.data.map((r) => ({
                    id: r.id,
                    type: camelizeTypeValues ? camelCase(r.type) : r.type,
                }));
            } else if (rel.data !== null) {
                entry.data = {
                    id: rel.data.id,
                    type: camelizeTypeValues ? camelCase(rel.data.type) : rel.data.type,
                };
            } else {
                entry.data = null;
            }
        }
        if (rel.links) entry.links = camelizeKeys ? camelizeNestedKeys(rel.links) : rel.links;
        if (rel.meta) entry.meta = camelizeKeys ? camelizeNestedKeys(rel.meta) : rel.meta;
        ret[name] = entry;
    }
    return ret;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- complex logic, refactor deferred
function extractEntities(
    data: JsonApiObject | JsonApiObject[],
    { camelizeKeys, camelizeTypeValues }: { camelizeKeys: boolean; camelizeTypeValues: boolean },
): Record<string, unknown> {
    const ret: Record<string, Record<string, unknown>> = {};
    const items = Array.isArray(data) ? data : [data];
    for (const elem of items) {
        const type = camelizeKeys ? camelCase(elem.type) : elem.type;
        ret[type] = ret[type] ?? {};
        ret[type][elem.id] = ret[type][elem.id] ?? { id: elem.id };
        const entity = ret[type][elem.id] as Record<string, unknown>;
        entity.type = camelizeTypeValues ? camelCase(elem.type) : elem.type;
        entity.attributes = camelizeKeys
            ? (camelizeNestedKeys(elem.attributes) as Record<string, unknown>)
            : elem.attributes;
        if (elem.links) {
            entity.links = {};
            for (const k of Object.keys(elem.links)) {
                (entity.links as Record<string, unknown>)[camelizeKeys ? camelCase(k) : k] =
                    elem.links[k];
            }
        }
        if (elem.relationships) {
            entity.relationships = extractRelationships(elem.relationships, {
                camelizeKeys,
                camelizeTypeValues,
            });
        }
        if (elem.meta) {
            entity.meta = camelizeKeys ? camelizeNestedKeys(elem.meta) : elem.meta;
        }
    }
    return ret;
}

function filterEndpointPath(endpoint: string): string {
    return endpoint.split('?')[0];
}

function extractMetaData(
    json: JsonApiResponse,
    endpoint: string,
    { camelizeKeys, filterEndpoint }: { camelizeKeys: boolean; filterEndpoint: boolean },
): Record<string, unknown> {
    const ret: Record<string, unknown> = { meta: {} };
    const meta = ret.meta as Record<string, unknown>;
    let metaObject: Record<string, unknown>;

    if (!filterEndpoint) {
        const base = filterEndpointPath(endpoint);
        meta[base] = {};
        (meta[base] as Record<string, unknown>)[endpoint.slice(base.length)] = {};
        metaObject = (meta[base] as Record<string, unknown>)[endpoint.slice(base.length)] as Record<
            string,
            unknown
        >;
    } else {
        meta[endpoint] = {};
        metaObject = meta[endpoint] as Record<string, unknown>;
    }

    metaObject.data = {};
    if (json.data) {
        const items = Array.isArray(json.data) ? json.data : [json.data];
        metaObject.data = items.map((o) => ({ id: o.id, type: o.type }));
    }
    if (json.links) {
        metaObject.links = json.links;
        (meta[filterEndpointPath(endpoint)] as Record<string, unknown>).links = json.links;
    }
    if (json.meta) {
        metaObject.meta = camelizeKeys
            ? (camelizeNestedKeys(json.meta) as Record<string, unknown>)
            : json.meta;
    }
    return ret;
}

export default function normalizeJsonApi<R = Record<string, unknown>>(
    json: JsonApiResponse,
    {
        filterEndpoint = true,
        camelizeKeys = true,
        camelizeTypeValues = true,
        endpoint,
    }: NormalizeOptions = {},
): R {
    const ret: Record<string, unknown> = {};
    if (json.data) merge(ret, extractEntities(json.data, { camelizeKeys, camelizeTypeValues }));
    if (json.included)
        merge(ret, extractEntities(json.included, { camelizeKeys, camelizeTypeValues }));
    if (endpoint) {
        const key = filterEndpoint ? filterEndpointPath(endpoint) : endpoint;
        merge(ret, extractMetaData(json, key, { camelizeKeys, filterEndpoint }));
    }
    return ret as R;
}
